// src/pages/UserCalendarPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import StaffLayout from "../components/StaffLayout"; // ✅ staff sidebar layout

/* ================= helpers ================= */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfCalendarGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const day = first.getDay(); // 0..6 (Sun..Sat)
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - day);
  gridStart.setHours(0, 0, 0, 0);
  return gridStart;
}
// ✅ end grid at the LAST WEEK that contains the month end (no forced 6th row)
function endOfCalendarGrid(monthDate) {
  const last = endOfMonth(monthDate);
  const day = last.getDay(); // 0..6
  const gridEnd = new Date(last);
  gridEnd.setDate(last.getDate() + (6 - day)); // move to Saturday
  gridEnd.setHours(23, 59, 59, 999);
  return gridEnd;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function formatMonthTitle(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}
function safeDate(dt) {
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? null : d;
}
function asEventWhen(e) {
  return (
    e?.start ||
    e?.datetime ||
    e?.date ||
    e?.start_time ||
    e?.when ||
    e?.starts_at ||
    e?.startAt ||
    e?.start_at
  );
}
function eventTitle(e) {
  return e?.title || e?.name || e?.summary || "Event";
}
function eventType(e) {
  return (e?.type || e?.category || e?.kind || "other")
    .toString()
    .toLowerCase();
}

function eventImageUrl(e) {
  return e?.image_url || e?.imageUrl || e?.image || e?.photo || null;
}

function toPublicUrl(url) {
  if (!url) return null;

  // If already full http(s) URL, keep it
  if (/^https?:\/\//i.test(url)) return url;

  // If backend gives "/uploads/xxx.jpg", keep it relative (same-origin)
  if (String(url).startsWith("/")) return url;

  return url;
}

/* ✅ Step A: safe JSON helper */
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/* ================= page ================= */
export default function UserCalendarPage() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.name || user?.username || "User";

  const [view, setView] = useState("month"); // UI only
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [selectedISO, setSelectedISO] = useState(() => toISODate(new Date()));

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  // ✅ days array only covers month grid up to end-of-week
  const days = useMemo(() => {
    const gridStart = startOfCalendarGrid(monthDate);
    const gridEnd = endOfCalendarGrid(monthDate);
    const arr = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
      arr.push(new Date(d));
    }
    return arr;
  }, [monthDate]);

  const monthStart = useMemo(() => startOfMonth(monthDate), [monthDate]);
  const monthEnd = useMemo(() => endOfMonth(monthDate), [monthDate]);
  const todayISO = useMemo(() => toISODate(new Date()), []);

  // ✅ Step A: Load events using authFetch("/api/...") + safe json
  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setEventsLoading(true);
        setEventsError("");

        const gridStart = startOfCalendarGrid(monthDate);
        const gridEnd = endOfCalendarGrid(monthDate);
        const from = toISODate(gridStart);
        const to = toISODate(gridEnd);

        const res = await authFetch(`/api/events?from=${from}&to=${to}`);
        const data = await safeJson(res);

        if (!res.ok) {
          if (!alive) return;
          setEvents([]);
          setEventsError(data?.error || `Failed to load events (status ${res.status})`);
          return;
        }

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.events)
          ? data.events
          : [];

        if (alive) setEvents(list);
      } catch (e) {
        if (alive) {
          setEvents([]);
          setEventsError(e?.message || "Failed to load events");
        }
      } finally {
        if (alive) setEventsLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [authFetch, monthDate]);

  // Group events by ISO day
  const eventsByDay = useMemo(() => {
    const map = new Map(); // iso -> events[]
    for (const e of events || []) {
      const when = asEventWhen(e);
      const d = safeDate(when);
      if (!d) continue;
      const iso = toISODate(d);
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(e);
    }

    for (const [iso, list] of map.entries()) {
      list.sort((a, b) => {
        const da = safeDate(asEventWhen(a))?.getTime?.() ?? 0;
        const db = safeDate(asEventWhen(b))?.getTime?.() ?? 0;
        return da - db;
      });
      map.set(iso, list);
    }

    return map;
  }, [events]);

  const selectedEvents = useMemo(() => {
    return eventsByDay.get(selectedISO) || [];
  }, [eventsByDay, selectedISO]);

  const selectedPrimaryEvent = selectedEvents[0] || null;

  function formatEventTime(e) {
    const d = safeDate(asEventWhen(e));
    if (!d) return "";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  function typeChipStyle(type) {
    switch ((type || "other").toLowerCase()) {
      case "meeting":
        return {
          background: "rgba(37,99,235,.10)",
          color: "#1d4ed8",
          border: "1px solid rgba(37,99,235,.18)",
        };
      case "food":
        return {
          background: "rgba(245,158,11,.14)",
          color: "#92400e",
          border: "1px solid rgba(245,158,11,.22)",
        };
      case "training":
        return {
          background: "rgba(124,58,237,.12)",
          color: "#6d28d9",
          border: "1px solid rgba(124,58,237,.20)",
        };
      default:
        return {
          background: "rgba(34,197,94,.10)",
          color: "#15803d",
          border: "1px solid rgba(34,197,94,.16)",
        };
    }
  }

  return (
    <StaffLayout>
      <div className="ucal-page">
        <style>{`
        :root{
          --bg:#f3f6fb;
          --card:#ffffff;
          --text:#0f172a;
          --muted:#64748b;
          --line:rgba(2,6,23,.08);
          --line-soft:rgba(2,6,23,.05);
          --shadow:0 10px 24px rgba(2,6,23,.06);
          --shadow-soft:0 6px 14px rgba(2,6,23,.05);
          --radius:14px;
          --blue:#2563eb;
          --blue-soft:rgba(37,99,235,.10);

          --cellSize: min(
            clamp(70px, 10vh, 118px),
            calc((100vw - 140px) / 7)
          );
        }

        html, body { height: 100%; }

        .ucal-page{
          height: calc(100dvh - 52px);
          background:
            radial-gradient(circle at top left, rgba(59,130,246,.05), transparent 28%),
            linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%);
          overflow: hidden;
        }

        .main{
          height: calc(100dvh - 52px);
          overflow: hidden;
          padding: 14px 18px 18px;
          scrollbar-width: none;
        }
        .main::-webkit-scrollbar{ width:0; height:0; }

        .topbar{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
        }
        .page-title{
          font-size: 26px;
          font-weight: 950;
          color: var(--text);
          margin: 0;
          letter-spacing: -.3px;
        }
        .page-sub{
          margin-top: 4px;
          font-size: 13px;
          color: var(--muted);
          font-weight: 700;
        }

        .top-actions{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .icon-btn{
          height: 38px;
          width: 38px;
          border-radius: 12px;
          border: 1px solid var(--line);
          background: #fff;
          box-shadow: var(--shadow-soft);
          cursor: pointer;
          font-weight: 900;
          color: var(--text);
          transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .icon-btn:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(2,6,23,.08);
          background: #f8fafc;
        }
        .avatar{
          height: 38px;
          width: 38px;
          border-radius: 999px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(37,99,235,.12);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
          font-weight: 950;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.55);
        }

        .cal-card{
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 18px;
          box-shadow: var(--shadow);
          overflow: hidden;
          height: calc(100% - 66px);
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .cal-head{
          padding: 12px 14px;
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid rgba(2,6,23,.06);
          flex-wrap: wrap;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
        }
        .cal-nav{
          display:flex;
          align-items:center;
          gap: 8px;
        }
        .cal-pill{
          border: 1px solid rgba(2,6,23,.08);
          background: #fff;
          height: 34px;
          padding: 0 12px;
          border-radius: 10px;
          font-weight: 900;
          color: var(--text);
          cursor:pointer;
          white-space: nowrap;
          box-shadow: 0 4px 10px rgba(2,6,23,.04);
          transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .cal-pill:hover{
          transform: translateY(-1px);
          box-shadow: 0 8px 14px rgba(2,6,23,.07);
        }
        .cal-pill.primary{
          background: rgba(37,99,235,.10);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
        }
        .cal-icon-btn{
          height: 34px;
          width: 40px;
          border-radius: 10px;
          border: 1px solid rgba(2,6,23,.08);
          background: #fff;
          cursor: pointer;
          font-weight: 950;
          box-shadow: 0 6px 12px rgba(2,6,23,.06);
          transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .cal-icon-btn:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(2,6,23,.08);
          background: #f8fafc;
        }
        .cal-month-title{
          font-size: 26px;
          font-weight: 950;
          color: var(--text);
          text-align: center;
          margin: 0;
          flex: 1;
          min-width: 220px;
          letter-spacing: -.4px;
        }
        .cal-head-right{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .view-toggle{
          display:flex;
          border: 1px solid rgba(2,6,23,.08);
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
          white-space: nowrap;
          box-shadow: 0 4px 10px rgba(2,6,23,.04);
        }
        .view-btn{
          height: 34px;
          padding: 0 14px;
          border: none;
          background: transparent;
          font-weight: 900;
          color: var(--muted);
          cursor:pointer;
          transition: background .15s ease, color .15s ease;
        }
        .view-btn.active{
          background: rgba(15,23,42,.60);
          color: #fff;
        }

        .cal-grid-wrap{
          padding: 10px 12px 12px;
          overflow: hidden;
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, #fcfdff 0%, #ffffff 100%);
        }

        .err{
          background: rgba(239,68,68,.06);
          border: 1px solid rgba(239,68,68,.14);
          color: #991b1b;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 850;
          font-size: 12px;
          margin-bottom: 10px;
        }

        .mobile-event-preview{
          display: none;
          margin-bottom: 10px;
        }

        .mobile-event-card{
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
          border: 1px solid rgba(2,6,23,.08);
          box-shadow: 0 8px 18px rgba(2,6,23,.06);
          overflow: hidden;
        }

        .mobile-event-thumb{
          width: 58px;
          height: 58px;
          border-radius: 12px;
          object-fit: cover;
          flex: 0 0 58px;
          border: 1px solid rgba(2,6,23,.08);
          background: #f8fafc;
        }

        .mobile-event-body{
          min-width: 0;
          flex: 1;
        }

        .mobile-event-kicker{
          font-size: 11px;
          font-weight: 900;
          color: #2563eb;
          margin-bottom: 3px;
          text-transform: uppercase;
          letter-spacing: .3px;
        }

        .mobile-event-title{
          font-size: 14px;
          font-weight: 950;
          color: #0f172a;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mobile-event-sub{
          margin-top: 4px;
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dow{
          display:grid;
          grid-template-columns: repeat(7, 1fr);
          border-left: 1px solid var(--line-soft);
          border-right: 1px solid var(--line-soft);
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          overflow: hidden;
        }
        .dow div{
          padding: 8px 6px;
          text-align:center;
          font-weight: 900;
          color: #334155;
          background: #f8fafc;
          border-top: 1px solid var(--line-soft);
          border-bottom: 1px solid var(--line-soft);
          font-size: 12px;
          letter-spacing: .2px;
        }

        .month-grid{
          display:grid;
          grid-template-columns: repeat(7, 1fr);
          grid-auto-rows: var(--cellSize);
          border: 1px solid var(--line-soft);
          border-top: none;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 12px;
          overflow: hidden;
          flex: 1;
          min-height: 0;
          background: #fff;
        }

        .day-cell{
          background: #fff;
          border-right: 1px solid var(--line-soft);
          border-bottom: 1px solid var(--line-soft);
          position: relative;
          cursor: pointer;
          overflow: hidden;
          padding: 8px;
          transition: background .18s ease, box-shadow .18s ease, transform .18s ease;
        }

        .cell-bg{
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
        }

        .cell-bg-overlay{
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,.66), rgba(0,0,0,.12));
          z-index: 1;
        }

        .cell-bg-title{
          position: absolute;
          left: 8px;
          right: 8px;
          bottom: 8px;
          z-index: 2;
          color: #fff;
          font-weight: 950;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow: 0 2px 4px rgba(0,0,0,.7);
        }

        .day-cell:nth-child(7n){ border-right: none; }
        .day-cell:hover{
          background: rgba(37,99,235,.03);
          box-shadow: inset 0 0 0 2px rgba(37,99,235,.08);
        }

        .day-top{
          position: relative;
          z-index: 3;
          display:flex;
          justify-content: flex-end;
          padding: 0;
          margin-bottom: 6px;
        }

        .day-num{
          font-weight: 950;
          color: var(--text);
          font-size: 13px;
          padding: 6px 9px;
          border-radius: 10px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-width: 28px;
          background: rgba(255,255,255,.92);
          border: 1px solid rgba(2,6,23,.05);
          box-shadow: 0 4px 8px rgba(2,6,23,.04);
        }

        .day-cell.outside{
          background: #fcfcfd;
        }
        .day-cell.outside .day-num{
          color: rgba(100,116,139,.55);
          background: rgba(248,250,252,.95);
        }
        .day-cell.today .day-num{
          background: rgba(37,99,235,.10);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
        }
        .day-cell.selected{
          background: rgba(37,99,235,.04);
          box-shadow: inset 0 0 0 2px rgba(37,99,235,.16);
        }

        .events-stack{
          position: relative;
          z-index: 3;
          display:flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
          min-height: 0;
        }

        .evt{
          font-size: 12px;
          font-weight: 900;
          padding: 6px 8px;
          border-radius: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          backdrop-filter: blur(2px);
          box-shadow: 0 2px 8px rgba(2,6,23,.05);
        }

        .evt-row{
          display:flex;
          align-items:center;
          gap: 8px;
        }

        .evt-thumb{
          width: 22px;
          height: 22px;
          border-radius: 7px;
          object-fit: cover;
          flex: 0 0 22px;
          border: 1px solid rgba(2,6,23,.10);
        }

        .evt-title{
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .evt small{
          font-weight: 900;
          opacity: .70;
          margin-left: 6px;
        }

        .evt-card{
          position: relative;
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(2,6,23,.08);
          background: #fff;
          box-shadow: 0 6px 12px rgba(2,6,23,.06);
          flex: 1;
          min-height: 0;
        }
        .evt-img{
          position:absolute;
          inset:0;
          width:100%;
          height:100%;
          object-fit: cover;
        }

        .evt-overlay{
          position:absolute;
          inset:0;
          background: linear-gradient(to top, rgba(0,0,0,.65), rgba(0,0,0,.15));
          z-index: 1;
        }

        .evt-label{
          position:absolute;
          left: 8px;
          right: 8px;
          bottom: 8px;
          z-index: 2;
          color: #fff;
          font-weight: 950;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow: 0 2px 4px rgba(0,0,0,.7);
        }

        .more{
          font-size: 12px;
          font-weight: 900;
          color: var(--muted);
          padding-left: 2px;
        }

        @media (max-width: 900px){
          :root{
            --cellSize: min(
              clamp(64px, 9vh, 102px),
              calc((100vw - 40px) / 7)
            );
          }
          .cal-month-title{ font-size: 20px; min-width: 160px; }
          .view-btn{ padding: 0 10px; }
          .page-title{ font-size: 22px; }
        }

        @media (max-width: 520px){
          :root{
            --cellSize: min(
              clamp(58px, 8.4vh, 90px),
              calc((100vw - 20px) / 7)
            );
          }

          .main{
            padding: 10px 10px 12px;
          }

          .topbar{
            margin-bottom: 10px;
          }

          .page-title{
            font-size: 22px;
          }

          .page-sub{
            font-size: 12px;
          }

          .cal-card{
            border-radius: 16px;
            height: calc(100% - 60px);
          }

          .cal-head{
            padding: 10px 10px;
            gap: 10px;
          }

          .cal-nav{
            gap: 6px;
          }

          .cal-pill,
          .cal-icon-btn{
            height: 32px;
          }

          .cal-pill{
            padding: 0 10px;
            font-size: 12px;
          }

          .cal-icon-btn{
            width: 36px;
          }

          .cal-month-title{
            font-size: 18px;
            text-align: left;
            min-width: auto;
          }

          .view-toggle{
            width: 100%;
          }

          .view-btn{
            flex: 1;
            padding: 0 8px;
            font-size: 12px;
          }

          .cal-grid-wrap{
            padding: 10px 10px 10px;
          }

          .mobile-event-preview{
            display: block;
          }

          .dow div{
            padding: 7px 4px;
            font-size: 11px;
          }

          .day-cell{
            padding: 6px;
          }

          .cell-bg,
          .cell-bg-overlay,
          .cell-bg-title{
            display: none;
          }

          .day-top{
            margin-bottom: 4px;
          }

          .day-num{
            font-size: 12px;
            min-width: 24px;
            padding: 5px 7px;
            border-radius: 8px;
          }

          .events-stack{
            gap: 3px;
          }

          .evt{
            font-size: 10px;
            padding: 3px 5px;
            border-radius: 7px;
            box-shadow: none;
          }

          .more{
            font-size: 10px;
          }
        }
      `}</style>

        <main className="main">
          <div className="topbar">
            <div>
              <h1 className="page-title">Calendar</h1>
              <div className="page-sub">Read-only view · {displayName}</div>
            </div>

            <div className="top-actions">
              <button className="icon-btn" type="button" title="Back" onClick={() => navigate("/my-schedule")}>
                ⟵
              </button>
              <div className="avatar" title={displayName}>
                {displayName
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((x) => x[0]?.toUpperCase())
                  .join("") || "U"}
              </div>
            </div>
          </div>

          <div className="cal-card">
            <div className="cal-head">
              <div className="cal-nav">
                <button
                  className="cal-icon-btn"
                  type="button"
                  onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
                  aria-label="Prev month"
                >
                  ‹
                </button>
                <button
                  className="cal-icon-btn"
                  type="button"
                  onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
                  aria-label="Next month"
                >
                  ›
                </button>
                <button
                  className="cal-pill primary"
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setMonthDate(now);
                    setSelectedISO(toISODate(now));
                  }}
                >
                  Today
                </button>
              </div>

              <h2 className="cal-month-title">{formatMonthTitle(monthDate)}</h2>

              <div className="cal-head-right">
                <div className="view-toggle" aria-label="Calendar view">
                  {["month", "week", "day", "list"].map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`view-btn ${view === k ? "active" : ""}`}
                      onClick={() => {
                        setView(k);
                        if (k !== "month") alert("Week / Day / List will be added later.");
                      }}
                    >
                      {k[0].toUpperCase() + k.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="cal-grid-wrap">
              {eventsError ? <div className="err">{eventsError}</div> : null}

              {!eventsLoading && selectedPrimaryEvent ? (
                <div className="mobile-event-preview">
                  <div className="mobile-event-card">
                    {toPublicUrl(eventImageUrl(selectedPrimaryEvent)) ? (
                      <img
                        className="mobile-event-thumb"
                        src={toPublicUrl(eventImageUrl(selectedPrimaryEvent))}
                        alt={eventTitle(selectedPrimaryEvent)}
                      />
                    ) : (
                      <div className="mobile-event-thumb" />
                    )}

                    <div className="mobile-event-body">
                      <div className="mobile-event-kicker">Selected day event</div>
                      <div className="mobile-event-title">
                        {eventTitle(selectedPrimaryEvent)}
                      </div>
                      <div className="mobile-event-sub">
                        {formatEventTime(selectedPrimaryEvent) || "Time not available"}
                        {selectedEvents.length > 1 ? ` • +${selectedEvents.length - 1} more` : ""}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="dow">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>

              <div className="month-grid">
                {days.map((d) => {
                  const iso = toISODate(d);
                  const outside = d < monthStart || d > monthEnd;
                  const isToday = iso === todayISO;
                  const isSelected = iso === selectedISO;

                  const list = eventsByDay.get(iso) || [];
                  const maxVisible = 1;
                  const visible = list.slice(0, maxVisible);
                  const remaining = Math.max(0, list.length - visible.length);

                  return (
                    <div
                      key={iso}
                      className={["day-cell", outside ? "outside" : "", isToday ? "today" : "", isSelected ? "selected" : ""].join(" ")}
                      onClick={() => setSelectedISO(iso)}
                      title="Events show inside each day"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setSelectedISO(iso);
                      }}
                    >
                      <div className="day-top">
                        <div className="day-num">{d.getDate()}</div>
                      </div>

                      {!eventsLoading && list.length > 0 && toPublicUrl(eventImageUrl(list[0])) ? (
                        <>
                          <img className="cell-bg" src={toPublicUrl(eventImageUrl(list[0]))} alt={eventTitle(list[0])} />
                          <div className="cell-bg-overlay" />
                          <div className="cell-bg-title">{eventTitle(list[0])}</div>
                        </>
                      ) : null}

                      <div className="events-stack">
                        {eventsLoading ? (
                          isSelected ? <div className="more">Loading…</div> : null
                        ) : visible.length ? (
                          <>
                            {visible.map((ev, idx) => {
                              const t = eventType(ev);
                              const style = typeChipStyle(t);

                              return (
                                <div key={`${iso}-${idx}`} className="evt" style={style} title={eventTitle(ev)}>
                                  {eventTitle(ev)}
                                </div>
                              );
                            })}
                            {remaining ? <div className="more">+{remaining} more</div> : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </div>
    </StaffLayout>
  );
}